// Step 1: Select supplier and enter note for a stock import.
import { type Dispatch, type SetStateAction } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Supplier {
  id: string;
  name: string;
}

interface Warehouse {
  id: string;
  name: string;
  isActive: boolean;
  isDefault: boolean;
}

export interface Step1Data {
  supplierId: string;
  warehouseId: string;
  note: string;
}

interface Props {
  data: Step1Data;
  onChange: Dispatch<SetStateAction<Step1Data>>;
}

export function ImportFormStepOne({ data, onChange }: Props) {
  const { data: suppliers = [] } = useQuery({
    queryKey: ["suppliers"],
    queryFn: () =>
      apiClient
        .get<{ data: Supplier[] }>("/v1/suppliers")
        .then((r) => r.data.data),
  });

  const { data: warehouses = [] } = useQuery({
    queryKey: ["warehouses"],
    queryFn: () =>
      apiClient
        .get<{ data: Warehouse[] }>("/v1/warehouses")
        .then((r) => r.data.data),
  });

  const activeWarehouses = warehouses.filter((w) => w.isActive);

  return (
    <div className="space-y-4 max-w-md">
      <div className="space-y-1.5">
        <Label htmlFor="warehouse">Kho nhập <span className="text-destructive">*</span></Label>
        <select
          id="warehouse"
          className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
          value={data.warehouseId}
          onChange={(e) => onChange((d) => ({ ...d, warehouseId: e.target.value }))}
        >
          <option value="">— Chọn kho —</option>
          {activeWarehouses.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}{w.isDefault ? " (mặc định)" : ""}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="supplier">Nhà cung cấp</Label>
        <select
          id="supplier"
          className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
          value={data.supplierId}
          onChange={(e) => onChange((d) => ({ ...d, supplierId: e.target.value }))}
        >
          <option value="">— Không chọn —</option>
          {suppliers.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="note">Ghi chú</Label>
        <Input
          id="note"
          placeholder="Ghi chú phiếu nhập…"
          value={data.note}
          onChange={(e) => onChange((d) => ({ ...d, note: e.target.value }))}
        />
      </div>
    </div>
  );
}
